import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import MainLayout from "@/components/layouts/MainLayout";
import { RotateCcw, Search, Pencil, Download, Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

const NotaCredito = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");

  // Consulta para obtener solo las notas de crédito
  const { data: notas = [], isLoading } = useQuery({
    queryKey: ['notas-credito', searchTerm],
    queryFn: async () => {
      if (!user) return [];

      let query = supabase
        .from('facturas')
        .select('*')
        .eq('document_type', 'nota_credito');

      if (searchTerm) {
        query = query.ilike('cliente', `%${searchTerm}%`);
      }

      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user
  });

  return (
    <MainLayout>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            Notas de Crédito
          </CardTitle>
          <CardDescription>
            Vista de todas las notas de crédito del sistema. Para crear nuevas notas, ve a la sección de Facturación.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Barra de búsqueda */}
            <div className="flex items-center space-x-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Buscar notas de crédito por cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Lista de notas de crédito */}
            <div className="space-y-2">
              {isLoading ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">Cargando notas de crédito...</p>
                </div>
              ) : notas.length === 0 ? (
                <div className="text-center py-8">
                  <RotateCcw className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">No hay notas de crédito creadas</p>
                  <p className="text-sm text-gray-400 mb-3">
                    Ve a la sección de Facturación y selecciona "Nota de Crédito" como tipo de documento
                  </p>
                  <Link to="/facturacion" className="inline-block">
                    <Button variant="outline" className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Ir a Facturación
                    </Button>
                  </Link>
                </div>
              ) : (
                notas.map((nota) => (
                  <div key={nota.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium">{nota.cliente}</h3>
                          <Badge variant={nota.status === 'approved' ? 'default' : 'secondary'}>
                            {nota.status === 'approved' ? 'Aprobada' : 'Borrador'}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600">NCF: {nota.ncf}</p>
                        <p className="text-sm text-gray-500">
                          Fecha: {new Date(nota.fecha).toLocaleDateString('es-DO')}
                        </p>
                        <p className="text-sm font-medium text-green-600">
                          Total: RD$ {new Intl.NumberFormat('es-DO').format(nota.total)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link to={`/facturacion?edit=${nota.id}`}>
                          <Button variant="outline" size="sm">
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Button variant="outline" size="sm">
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default NotaCredito; 